import { FeorNumber } from 'src/DTO/FeorNumber.type';
import { Job, JobApplication } from 'src/DTO/Job.type';
import { JobConfirmation } from 'src/DTO/JobConfirmation.type';
import { JobOffer } from 'src/DTO/JobOffer.type';
import { useApi } from './useApi';
import { BaseRequest, BaseHookRequest } from './useApi.types';
import {
  AcceptConfirmationPayload,
  CreateJobOfferPayload,
  CreateJobPayload,
  CreateJobOfferBatchPayload,
  GetJobByIdPayload,
  TerminateWorkerFromJobPayload,
  CalculateJobWagePayload,
  SearchFeorPayload,
  JobWageResponse,
  TerminateJobPayload,
  RemoveLanguagesPayload,
  GenerateFacebookPostPayload,
  GenerateFacebookPostResponse,
  TerminateWorkersFromJobPayload,
  MinWageResponse,
  GetJobByIdResponse,
  HandleJobApplicationPayload,
  JobOfferPayload,
  ParsedJobsFromFeedResponse,
  ParsedJobsFromFeedPayload,
  CachedJobsResponse,
} from './useJob.types';
import { useStoreState } from 'src/stores';

export const useCreateJob: BaseHookRequest<Job, CreateJobPayload> = params => {
  const [{ postRequest }, state] = useApi<Job>(params);
  const createJob: BaseRequest<CreateJobPayload> = details => {
    postRequest<CreateJobPayload>('/manager/job/save', details?.variables);
  };

  return [createJob, state];
};

export const useGenerateFacebookPost: BaseHookRequest<
  GenerateFacebookPostResponse,
  GenerateFacebookPostPayload
> = params => {
  const [{ postRequest }, state] = useApi<GenerateFacebookPostResponse>(params);
  const generateFacebookPost: BaseRequest<GenerateFacebookPostPayload> = details => {
    postRequest<GenerateFacebookPostPayload>(
      '/manager/job/generate/facebook-post',
      details?.variables,
    );
  };

  return [generateFacebookPost, state];
};

export const useCreateJobOffer: BaseHookRequest<undefined, CreateJobOfferPayload> = params => {
  const [{ postRequest }, state] = useApi<undefined>(params);
  const createJobOffer: BaseRequest<CreateJobOfferPayload> = details => {
    postRequest<CreateJobOfferPayload>('/manager/offer/create', details?.variables);
  };

  return [createJobOffer, state];
};

export const useCreateJobOfferBatch: BaseHookRequest<undefined, CreateJobOfferBatchPayload> =
  params => {
    const [{ postRequest }, state] = useApi<undefined>(params);
    const createJobOfferBatch: BaseRequest<CreateJobOfferBatchPayload> = details => {
      postRequest<CreateJobOfferBatchPayload>('/manager/offer/create/batch', details?.variables);
    };

    return [createJobOfferBatch, state];
  };

export const useGetAllJobConfirmations: BaseHookRequest<JobConfirmation[], null> = params => {
  const [{ getRequest }, state] = useApi(params);
  const getActiveJobConfirmations = () => getRequest('/manager/confirmation/pending');
  return [getActiveJobConfirmations, state];
};

export const useAcceptConfirmation: BaseHookRequest<JobConfirmation, AcceptConfirmationPayload> =
  params => {
    const [{ postRequest }, state] = useApi(params);
    const acceptConfirmation: BaseRequest<AcceptConfirmationPayload> = details =>
      postRequest<AcceptConfirmationPayload>(
        `/manager/confirmation/accept/${details?.variables.confirmationId}`,
        details?.variables,
      );
    return [acceptConfirmation, state];
  };

export const useGetJobs: BaseHookRequest<Job[], null> = params => {
  const [{ getRequest }, state] = useApi<Job[]>(params);
  const getJobs = () => {
    getRequest('/manager/job/all');
  };
  return [getJobs, state];
};

export const useGetJobOffers: BaseHookRequest<JobOffer[], JobOfferPayload> = params => {
  const [{ getRequest }, state] = useApi<JobOffer[]>(params);
  const getJobOffers: BaseRequest<JobOfferPayload> = details => {
    getRequest(`/manager/offer/${details?.variables.jobId}`);
  };
  return [getJobOffers, state];
};

export const useGetJobById: BaseHookRequest<GetJobByIdResponse, GetJobByIdPayload> = params => {
  const [{ getRequest }, state] = useApi<GetJobByIdResponse>(params);
  const getJobById: BaseRequest<GetJobByIdPayload> = details => {
    getRequest(`/manager/job/details/${details?.variables.jobId}`);
  };
  return [getJobById, state];
};

export const useTerminateWorkers: BaseHookRequest<undefined, TerminateWorkersFromJobPayload> =
  params => {
    const [{ postRequest }, state] = useApi(params);
    const terminateWorkers: BaseRequest<TerminateWorkersFromJobPayload> = details =>
      postRequest<TerminateWorkersFromJobPayload>(
        '/manager/job/terminateworkers',
        details?.variables,
      );
    return [terminateWorkers, state];
  };

export const useTerminateWorker: BaseHookRequest<undefined, TerminateWorkerFromJobPayload> =
  params => {
    const [{ postRequest }, state] = useApi(params);
    const terminateWorker: BaseRequest<TerminateWorkerFromJobPayload> = details =>
      postRequest<TerminateWorkerFromJobPayload>(
        '/manager/job/terminateworker',
        details?.variables,
      );
    return [terminateWorker, state];
  };

export const useCalculateJobWage: BaseHookRequest<JobWageResponse, CalculateJobWagePayload> =
  params => {
    const [{ postRequest }, state] = useApi(params);
    const user = useStoreState(state => state.user.data);
    const rake = user && user.companies ? Number(user.companies[0].rake) : undefined;

    const calculateJobWage: BaseRequest<CalculateJobWagePayload> = details =>
      postRequest<CalculateJobWagePayload>('/manager/job/calculateJobWage', {
        ...details?.variables,
        rake,
      });
    return [calculateJobWage, state];
  };

export const useSearchFeorNumber: BaseHookRequest<FeorNumber[], SearchFeorPayload> = params => {
  const [{ postRequest }, state] = useApi(params);
  const searchFeor: BaseRequest<SearchFeorPayload> = details =>
    postRequest<SearchFeorPayload>('/manager/job/searchFeor', details?.variables);
  return [searchFeor, state];
};

export const useGetJobApplications: BaseHookRequest<JobApplication[], GetJobByIdPayload> =
  params => {
    const [{ getRequest }, state] = useApi<JobApplication[]>(params);
    const getJobApplications: BaseRequest<GetJobByIdPayload> = details => {
      getRequest(`/manager/application/${details?.variables.jobId}`);
    };
    return [getJobApplications, state];
  };

export const useHandleJobApplication: BaseHookRequest<undefined, HandleJobApplicationPayload> =
  params => {
    const [{ postRequest }, state] = useApi<undefined>(params);
    const handleApplication: BaseRequest<HandleJobApplicationPayload> = details => {
      postRequest(`/manager/application/handle/${details?.variables.applicationId}`, {
        action: details?.variables.action,
      });
    };
    return [handleApplication, state];
  };

export const useTerminateJob: BaseHookRequest<undefined, TerminateJobPayload> = params => {
  const [{ postRequest }, state] = useApi<undefined>(params);

  const terminateRole: BaseRequest<TerminateJobPayload> = details => {
    postRequest<TerminateJobPayload>('/manager/job/terminate', details?.variables);
  };

  return [terminateRole, state];
};

export const useRemoveLanguages: BaseHookRequest<undefined, RemoveLanguagesPayload> = params => {
  const [{ postRequest }, state] = useApi<undefined>(params);
  const removeLanguage: BaseRequest<RemoveLanguagesPayload> = details => {
    postRequest<RemoveLanguagesPayload>('/manager/job/language/remove', details?.variables);
  };

  return [removeLanguage, state];
};

export const useGetMinWage: BaseHookRequest<MinWageResponse, null> = params => {
  const [{ getRequest }, state] = useApi(params);
  const getMinWage = () => getRequest<MinWageResponse>('/manager/job/minWage');
  return [getMinWage, state];
};

export const useFetchJobsFromFeed: BaseHookRequest<
  ParsedJobsFromFeedResponse,
  ParsedJobsFromFeedPayload
> = params => {
  const [{ postRequest }, state] = useApi<ParsedJobsFromFeedResponse>(params);
  const fetchJobsFromFeed: BaseRequest<ParsedJobsFromFeedPayload> = details => {
    postRequest('/manager/job/mass-import/feed-url', details?.variables, 10000 * 30);
  };
  return [fetchJobsFromFeed, state];
};

export const useFetchJobsFromCache: BaseHookRequest<CachedJobsResponse, void> = params => {
  const [{ getRequest }, state] = useApi<CachedJobsResponse>(params);
  const fetchJobsFromCache: BaseRequest<void> = () => {
    getRequest('/manager/job/mass-import/feed');
  };
  return [fetchJobsFromCache, state];
};

export const useClearJobFeedCache: BaseHookRequest<undefined, void> = params => {
  const [{ deleteRequest }, state] = useApi<undefined>(params);
  const clearJobFeedCache: BaseRequest<void> = () => {
    deleteRequest('/manager/job/mass-import/feed/cache');
  };
  return [clearJobFeedCache, state];
};
